import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMediaQuery, useTheme } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { Check, ChevronDown, ChevronUp, Minus } from 'lucide-react';

import { usePedestrianLayerTypeEnabled } from '../../hooks/useIdlc1129Features';
import { layerTypeKeys, layerTypeLabel } from '../../utils/layerTypeLabel';
import { LayerTypeIcon } from '../project-settings/components/LayerTypeIcon';
import { LAYER_TYPES } from '../project-settings/types';
import { PolygonSelectionBar } from './PolygonSelectionBar';
import { ImportedLayer } from './cad-import/importCad';
import { CadPolygon } from './types';

/**
 * On a phone the sidebar is a sheet over the bottom of the plan: collapsed to this height (a bar and
 * the footer) the plan above it can be drawn on; opened it covers the plan.
 */
export const MOBILE_SHEET_COLLAPSED = 104;

interface CadPolygonSidebarProps {
  polygons: CadPolygon[];
  activePolygonIndex: number | null;
  drawingPointsCount: number;
  onUpdatePolygon: (id: string, updates: Partial<CadPolygon>) => void;
  onDeletePolygon: (id: string) => void;
  /** Multi-select: the same change to several polygons, and deleting them, each one undo step. */
  onUpdatePolygons: (ids: string[], updates: Partial<CadPolygon>) => void;
  onDeletePolygons: (ids: string[]) => void;
  /** The ticked polygons (kept by the editor, so the plan shows them too). */
  selectedIds: Set<string>;
  onSelectedIdsChange: (ids: Set<string>) => void;
  onSelectPolygon: (index: number | null) => void;
  /** Layers of an imported drawing, with the polygons found and the type recognised on each. */
  importedLayers: ImportedLayer[] | null;
  /** Layers whose polygons are in the plan. */
  createdLayers: Set<string>;
  onCreatePolygonsFromLayer: (layerName: string) => void;
  /** Creates every recognised, visible layer at once. */
  onCreateAllLayers: () => void;
  onLayerHover: (layerName: string | null) => void;
  /** Replace the project name field and the back / create buttons (plan editing in the workspace). */
  header: ReactNode;
  footer: ReactNode;
}

const LAYER_TYPE_KEYS = Object.keys(LAYER_TYPES) as Array<keyof typeof LAYER_TYPES>;

export function CadPolygonSidebar({
  polygons,
  activePolygonIndex,
  drawingPointsCount,
  onUpdatePolygon,
  onDeletePolygon,
  onUpdatePolygons,
  onDeletePolygons,
  selectedIds,
  onSelectedIdsChange,
  onSelectPolygon,
  importedLayers,
  createdLayers,
  onCreatePolygonsFromLayer,
  onCreateAllLayers,
  onLayerHover,
  header,
  footer,
}: CadPolygonSidebarProps) {
  const { t } = useTranslation();
  const pedestrianLayerType = usePedestrianLayerTypeEnabled();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sheetOpen, setSheetOpen] = useState(false);
  const collapsed = isMobile && !sheetOpen;
  // An imported drawing with nothing created from it yet: on a phone, open the list where its layers are.
  useEffect(() => {
    if (isMobile && importedLayers?.length && polygons.length === 0) setSheetOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importedLayers, isMobile]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedTypeId, setExpandedTypeId] = useState<string | null>(null);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingNameDraft, setEditingNameDraft] = useState<string>('');
  const [dropdownPos, setDropdownPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [layersSectionOpen, setLayersSectionOpen] = useState(true);
  const [polygonsSectionOpen, setPolygonsSectionOpen] = useState(true);
  const [undefinedSectionOpen, setUndefinedSectionOpen] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  // Local string state for height inputs — allows free typing, commits only valid values
  const [heightInputs, setHeightInputs] = useState<Record<string, string>>({});
  // Multi-select: ticked polygons (kept by the editor), and the last one ticked (Shift+click ticks
  // the range to it).
  const setSelectedIds = onSelectedIdsChange;
  const selectionAnchor = useRef<string | null>(null);

  const hasLayers = importedLayers && importedLayers.length > 0;

  // Listed in drawing order: new polygons at the bottom, and a renamed polygon stays where it is.

  const toggleSelected = (id: string, range: boolean) => {
    // The range is worked out here, before the anchor moves to this row (not in the state updater,
    // which runs later and would see the new anchor).
    const anchor = selectionAnchor.current;
    const order = polygons.map((p) => p.id);
    const rangeIds =
      range && anchor && order.includes(anchor)
        ? order.slice(
            Math.min(order.indexOf(anchor), order.indexOf(id)),
            Math.max(order.indexOf(anchor), order.indexOf(id)) + 1,
          )
        : null;
    selectionAnchor.current = id;
    // Ticking rows closes an open row, so its details do not push the list around.
    setExpandedId(null);
    setExpandedTypeId(null);
    const next = new Set(selectedIds);
    if (rangeIds) rangeIds.forEach((rowId) => next.add(rowId));
    else if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };
  const selectedPolygons = polygons.filter((p) => selectedIds.has(p.id));
  const allSelected = polygons.length > 0 && selectedPolygons.length === polygons.length;

  const checkbox = (checked: boolean | 'mixed', onToggle: (e: React.MouseEvent) => void, label: string) => (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(e);
      }}
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border transition-colors ${
        checked ? 'border-lime-400 bg-lime-400' : 'border-white/30 hover:border-white/60'
      }`}
    >
      {checked === 'mixed' ? (
        <Minus className="h-3 w-3 text-black" />
      ) : checked ? (
        <Check className="h-3 w-3 text-black" />
      ) : null}
    </button>
  );

  // Layers with polygons to create (recognised types first), and the rest: annotation, lighting,
  // hidden layers and layers without closed areas.
  const { usableLayers, undefinedLayers, recognisedCount } = useMemo(() => {
    if (!importedLayers) return { usableLayers: [], undefinedLayers: [], recognisedCount: 0 };
    const usable = importedLayers.filter((l) => l.polygons.length > 0 && !l.hidden && l.cls.role !== 'ignore');
    const usableNames = new Set(usable.map((l) => l.name));
    const typed = (l: ImportedLayer) => (l.cls.type === 'Unspecified' ? 1 : 0);
    return {
      usableLayers: [...usable].sort((a, b) => typed(a) - typed(b)),
      undefinedLayers: importedLayers.filter((l) => !usableNames.has(l.name)),
      recognisedCount: usable
        .filter((l) => l.cls.type !== 'Unspecified' && !createdLayers.has(l.name))
        .reduce((n, l) => n + l.polygons.length, 0),
    };
  }, [importedLayers, createdLayers]);

  const handleRowClick = (index: number, polygon: CadPolygon) => {
    onSelectPolygon(index);
    setExpandedId((prev) => (prev === polygon.id ? null : polygon.id));
    setExpandedTypeId(null);
  };

  const handleTypeClick = (e: React.MouseEvent, polygon: CadPolygon, index: number) => {
    e.stopPropagation();
    onSelectPolygon(index);
    if (expandedTypeId === polygon.id) {
      setExpandedTypeId(null);
    } else {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      // Estimated dropdown height: header (~30px) + LAYER_TYPE_KEYS rows (~32px each) + padding.
      const dropdownHeight = 30 + LAYER_TYPE_KEYS.length * 32 + 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < dropdownHeight + 8) {
        setDropdownPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left });
      } else {
        setDropdownPos({ top: rect.bottom + 4, left: rect.left });
      }
      setExpandedTypeId(polygon.id);
    }
  };

  const handleTypeSelect = (polygonId: string, type: keyof typeof LAYER_TYPES) => {
    onUpdatePolygon(polygonId, { type });
    setExpandedTypeId(null);
  };

  const handleNameDoubleClick = (e: React.MouseEvent, polygonId: string) => {
    e.stopPropagation();
    const current = polygons.find((p) => p.id === polygonId);
    setEditingNameDraft(current?.name ?? '');
    setEditingNameId(polygonId);
    setTimeout(() => nameInputRef.current?.select(), 0);
  };

  // Commit draft on blur/Enter. Empty names are not allowed — revert to previous name.
  const commitNameEdit = (polygonId: string) => {
    const trimmed = editingNameDraft.trim();
    if (trimmed.length > 0) {
      onUpdatePolygon(polygonId, { name: trimmed });
    }
    setEditingNameId(null);
    setEditingNameDraft('');
  };

  const cancelNameEdit = () => {
    setEditingNameId(null);
    setEditingNameDraft('');
  };

  return (
    <div
      className={`fixed bg-[#232323] text-gray-300 flex flex-col z-10 shadow-2xl pointer-events-auto ${
        isMobile
          ? `left-0 right-0 bottom-0 rounded-t-2xl ${sheetOpen ? 'top-[64px]' : ''}`
          : 'top-[64px] bottom-0 right-0 h-[calc(100vh-64px)] w-[455px] rounded-l-2xl'
      }`}
    >
      {isMobile && (
        <button
          onClick={() => setSheetOpen(!sheetOpen)}
          aria-expanded={sheetOpen}
          className="flex-shrink-0 h-11 w-full flex items-center justify-between px-6 border-b border-gray-800 text-xs"
        >
          <span className="text-gray-300">
            {t('projects.cadProject.polygons', 'Polygons')} ({polygons.length})
          </span>
          <span className="flex items-center gap-1 text-[#B3EC49]">
            {sheetOpen ? t('projects.cadProject.showPlan') : t('projects.cadProject.showList')}
            {sheetOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </span>
        </button>
      )}

      {/* Header */}
      <div className={`flex-shrink-0 px-6 py-4 border-b border-gray-800 ${collapsed ? 'hidden' : ''}`}>{header}</div>

      {/* Scrollable content: Layers + Polygons */}
      <div className={`flex-1 overflow-y-auto custom-scrollbar ${collapsed ? 'hidden' : ''}`}>
        {/* ── Layers Section ─────────────────────────────────────────────── */}
        {hasLayers && (
          <div className="border-b border-[#313131]">
            {/* Section header */}
            <button
              onClick={() => setLayersSectionOpen(!layersSectionOpen)}
              className="w-full pl-4 pr-6 py-2.5 flex items-center justify-between hover:bg-[#2a2a2a] transition"
            >
              <div className="flex items-center gap-2">
                <svg
                  className={`w-3 h-3 text-gray-500 transition-transform ${layersSectionOpen ? 'rotate-90' : ''}`}
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M8 5l8 7-8 7z" />
                </svg>
                <svg
                  className="w-4 h-4 text-gray-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="12 2 2 7 12 12 22 7 12 2" />
                  <polyline points="2 17 12 22 22 17" />
                  <polyline points="2 12 12 17 22 12" />
                </svg>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                  {t('projects.cadProject.layers', 'Layers')}
                </span>
                <span className="text-[10px] text-gray-600">({importedLayers!.length})</span>
              </div>
            </button>

            {/* Create every recognised layer at once */}
            {layersSectionOpen && recognisedCount > 0 && (
              <div className="px-4 pb-2">
                <button
                  onClick={onCreateAllLayers}
                  className="w-full h-8 rounded-lg text-xs font-medium text-black bg-lime-400 hover:bg-lime-500 transition"
                >
                  {t('projects.cadProject.createAllLayers', { count: recognisedCount })}
                </button>
              </div>
            )}

            {/* Description / disclaimer */}
            {layersSectionOpen && (
              <div className="px-4 pb-2 space-y-1">
                <p className="text-[12px] text-red-500/80 leading-relaxed">
                  {t('projects.cadProject.layersDisclaimer')}
                </p>
                <p className="text-[12px] text-gray-500 leading-relaxed">{t('projects.cadProject.layersHowTo')}</p>
              </div>
            )}

            {/* Usable layer rows */}
            {layersSectionOpen && (
              <div className="divide-y divide-[#2a2a2a] max-h-[30vh] overflow-y-auto">
                {usableLayers.map((layer) => {
                  const created = createdLayers.has(layer.name);
                  const typed = layer.cls.type !== 'Unspecified';
                  return (
                    <div
                      key={layer.name}
                      onMouseEnter={() => onLayerHover(layer.name)}
                      onMouseLeave={() => onLayerHover(null)}
                      className="h-[36px] pl-8 pr-4 flex items-center justify-between transition hover:bg-[#313131]"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {typed ? (
                          <Tooltip
                            title={t('projects.cadProject.recognisedAs', { type: layer.cls.type })}
                            placement="left"
                          >
                            <span className="layer-icon-container shrink-0 flex">
                              <LayerTypeIcon type={layer.cls.type} style={{ width: 14, height: 14 }} />
                            </span>
                          </Tooltip>
                        ) : (
                          <svg
                            className="w-3.5 h-3.5 shrink-0 text-gray-500"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polygon points="12 2 2 7 12 12 22 7 12 2" />
                          </svg>
                        )}
                        <span className="text-sm text-gray-200 truncate">{layer.name}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] text-lime-400 bg-lime-400/10 px-1.5 py-0.5 rounded">
                          {layer.polygons.length}
                        </span>
                        {created ? (
                          <span className="px-2 py-0.5 text-[10px] text-gray-400">
                            {t('projects.cadProject.layerCreated')}
                          </span>
                        ) : (
                          <button
                            onClick={() => onCreatePolygonsFromLayer(layer.name)}
                            className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-black bg-lime-400 rounded hover:bg-lime-500 transition"
                          >
                            <svg
                              className="w-2.5 h-2.5"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                            >
                              <line x1="12" y1="5" x2="12" y2="19" />
                              <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            {t('projects.cadProject.createBtn', 'Create')}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Undefined layers group */}
                {undefinedLayers.length > 0 && (
                  <>
                    <button
                      onClick={() => setUndefinedSectionOpen(!undefinedSectionOpen)}
                      className="w-full h-[36px] pl-8 pr-6 flex items-center justify-between hover:bg-[#2a2a2a] transition"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <svg
                          className={`w-2.5 h-2.5 text-gray-600 transition-transform ${undefinedSectionOpen ? 'rotate-90' : ''}`}
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M8 5l8 7-8 7z" />
                        </svg>
                        <span className="text-xs text-gray-500 italic">
                          {t('projects.cadProject.undefinedLayers', 'Undefined')}
                        </span>
                        <span className="text-[10px] text-gray-600">({undefinedLayers.length})</span>
                      </div>
                    </button>
                    {undefinedSectionOpen &&
                      undefinedLayers.map(({ name: layerName }) => (
                        <div
                          key={layerName}
                          onMouseEnter={() => onLayerHover(layerName)}
                          onMouseLeave={() => onLayerHover(null)}
                          className="h-[32px] pl-12 pr-6 flex items-center justify-between transition opacity-40"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <svg
                              className="w-3 h-3 shrink-0 text-gray-600"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polygon points="12 2 2 7 12 12 22 7 12 2" />
                            </svg>
                            <span className="text-xs text-gray-500 truncate">{layerName}</span>
                          </div>
                          <span className="text-[10px] text-gray-600">—</span>
                        </div>
                      ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Polygons Section ───────────────────────────────────────────── */}
        <div className="border-b border-[#313131]">
          {/* Section header */}
          <button
            onClick={() => setPolygonsSectionOpen(!polygonsSectionOpen)}
            className="w-full pl-4 pr-6 py-2.5 flex items-center justify-between hover:bg-[#2a2a2a] transition"
          >
            <div className="flex items-center gap-2">
              <svg
                className={`w-3 h-3 text-gray-500 transition-transform ${polygonsSectionOpen ? 'rotate-90' : ''}`}
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M8 5l8 7-8 7z" />
              </svg>
              <svg
                className="w-4 h-4 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5" />
              </svg>
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                {t('projects.cadProject.polygons', 'Polygons')}
              </span>
              <span className="text-[10px] text-gray-600">({polygons.length})</span>
            </div>
          </button>

          {polygonsSectionOpen && (
            <>
              {/* Column Headers */}
              <div className="pl-4 pr-6 py-2 flex items-center justify-between border-b border-[#313131] flex-shrink-0 sticky top-0 bg-[#232323] z-[1]">
                <div className="flex items-center gap-3">
                  {checkbox(
                    allSelected ? true : selectedPolygons.length > 0 ? 'mixed' : false,
                    () => setSelectedIds(allSelected ? new Set() : new Set(polygons.map((p) => p.id))),
                    t('projects.cadProject.selection.selectAll', 'Select all'),
                  )}
                  <Tooltip title={t('workspace.typeTooltip', 'Type of layer (ex. Road, Sidewalk)')} arrow>
                    <span className="text-[10px] text-gray-500 w-6 text-center cursor-help">{t('workspace.type')}</span>
                  </Tooltip>
                  <span className="text-[10px] text-gray-500">{t('common.name')}</span>
                </div>
                <div className="flex items-center gap-4">
                  <Tooltip title={t('projects.cadProject.heightTooltip', 'Height of 3D object in scene')} arrow>
                    <span className="text-[10px] text-gray-500 w-12 text-center cursor-help">H (m)</span>
                  </Tooltip>
                  <span className="text-[10px] text-gray-500 w-6" />
                </div>
              </div>

              {selectedPolygons.length > 0 && (
                <PolygonSelectionBar
                  selected={selectedPolygons}
                  onClear={() => setSelectedIds(new Set())}
                  onUpdate={(updates) => onUpdatePolygons([...selectedIds], updates)}
                  onDelete={() => {
                    onDeletePolygons([...selectedIds]);
                    setSelectedIds(new Set());
                  }}
                />
              )}

              <div className="divide-y divide-[#313131]">
                {/* Empty state */}
                {polygons.length === 0 && drawingPointsCount === 0 && (
                  <div className="text-center py-12 text-gray-600 text-sm">{t('projects.cadProject.noPolygons')}</div>
                )}

                {/* Polygon rows */}
                {polygons.map((p, i) => {
                  const isActive = i === activePolygonIndex;
                  const isExpanded = expandedId === p.id;
                  const isEditingName = editingNameId === p.id;

                  return (
                    <React.Fragment key={p.id}>
                      {/* Row */}
                      <div
                        onClick={() => handleRowClick(i, p)}
                        className={`h-[36px] pl-4 pr-6 flex items-center justify-between hover:bg-[#313131] transition cursor-pointer ${
                          isActive ? 'bg-lime-400/10' : ''
                        }`}
                      >
                        {/* Left: Type + Color + Name */}
                        <div className="flex items-center gap-3 overflow-hidden mr-4">
                          {checkbox(
                            selectedIds.has(p.id),
                            (e) => toggleSelected(p.id, e.shiftKey),
                            t('projects.cadProject.selection.select', {
                              name: p.name,
                              defaultValue: 'Select {{name}}',
                            }),
                          )}
                          {/* Type icon */}
                          <div className="relative">
                            <button
                              onClick={(e) => handleTypeClick(e, p, i)}
                              className="w-6 h-6 flex items-center justify-center border border-black bg-transparent rounded hover:border-gray-500 transition shrink-0"
                              title={`Type: ${p.type}`}
                            >
                              <LayerTypeIcon
                                type={p.type as keyof typeof LAYER_TYPES}
                                style={{ width: 14, height: 14, color: 'white' }}
                              />
                            </button>
                          </div>

                          {/* Name */}
                          {isEditingName ? (
                            <input
                              ref={nameInputRef}
                              type="text"
                              value={editingNameDraft}
                              onChange={(e) => setEditingNameDraft(e.target.value)}
                              onBlur={() => commitNameEdit(p.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitNameEdit(p.id);
                                else if (e.key === 'Escape') cancelNameEdit();
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 min-w-0 text-sm bg-black border border-lime-500 rounded px-1 text-white outline-none"
                              autoFocus
                            />
                          ) : (
                            <span
                              className="font-medium text-gray-100 truncate pl-1 cursor-text"
                              style={{ fontSize: '14px' }}
                              title={p.name}
                              onDoubleClick={(e) => handleNameDoubleClick(e, p.id)}
                            >
                              {p.name}
                            </span>
                          )}
                        </div>

                        {/* Right: Height + LMN + Norm + Delete */}
                        <div className="flex items-center gap-4 shrink-0">
                          {/* Height input */}
                          <Tooltip title={t('projects.cadProject.heightTooltip', 'Height of 3D object in scene')} arrow>
                            <input
                              type="number"
                              min={0}
                              step={0.5}
                              value={heightInputs[p.id] ?? p.height}
                              onChange={(e) => {
                                e.stopPropagation();
                                const raw = e.target.value;
                                setHeightInputs((prev) => ({ ...prev, [p.id]: raw }));
                                const num = parseFloat(raw);
                                if (!isNaN(num) && num >= 0) {
                                  onUpdatePolygon(p.id, { height: num });
                                }
                              }}
                              onBlur={() => {
                                const raw = heightInputs[p.id];
                                if (raw === undefined) return;
                                const num = parseFloat(raw);
                                if (isNaN(num) || num < 0) {
                                  // Reset to polygon's current value
                                  setHeightInputs((prev) => {
                                    const next = { ...prev };
                                    delete next[p.id];
                                    return next;
                                  });
                                } else {
                                  setHeightInputs((prev) => {
                                    const next = { ...prev };
                                    delete next[p.id];
                                    return next;
                                  });
                                }
                              }}
                              onFocus={() => onSelectPolygon(i)}
                              onClick={(e) => e.stopPropagation()}
                              className={`w-12 h-6 px-1 rounded border bg-black text-green01 text-xs outline-none text-right tabular-nums ${
                                heightInputs[p.id] !== undefined &&
                                (isNaN(parseFloat(heightInputs[p.id])) || parseFloat(heightInputs[p.id]) < 0)
                                  ? 'border-red-500'
                                  : 'border-white/10 focus:border-green01'
                              }`}
                            />
                          </Tooltip>

                          {/* Delete */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeletePolygon(p.id);
                            }}
                            className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-400/20 text-gray-500 hover:text-red-400 transition shrink-0"
                            title={t('common.delete')}
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Expanded section */}
                      {isExpanded && (
                        <div className="bg-[#313131] shadow-inner">
                          {/* Vertex coordinates list */}
                          <div className="px-6 py-3">
                            <p className="text-xs text-gray-500 mb-2">
                              {t('projects.cadProject.vertices', 'Vertices')} ({p.points.length})
                            </p>
                            <div className="max-h-[200px] overflow-y-auto custom-scrollbar">
                              <div className="grid grid-cols-2 gap-1">
                                {p.points.map(([x, y], vi) => (
                                  <div key={vi} className="rounded border border-white/5 bg-black/30 px-1.5 py-1">
                                    <div className="flex items-center justify-between mb-0.5">
                                      <span className="text-[9px] text-gray-500 font-medium">#{vi + 1}</span>
                                      {p.points.length > 3 && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const newPoints = p.points.filter((_, i) => i !== vi);
                                            onUpdatePolygon(p.id, { points: newPoints });
                                          }}
                                          className="w-3.5 h-3.5 flex items-center justify-center rounded text-gray-600 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                                          title={t('common.delete')}
                                        >
                                          <svg
                                            className="w-2.5 h-2.5"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <line x1="18" y1="6" x2="6" y2="18" />
                                            <line x1="6" y1="6" x2="18" y2="18" />
                                          </svg>
                                        </button>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-0.5">
                                      <span className="text-[9px] text-red-400 shrink-0">X</span>
                                      <input
                                        type="number"
                                        step={0.01}
                                        value={parseFloat(x.toFixed(2))}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          const newPoints = [...p.points] as [number, number][];
                                          newPoints[vi] = [parseFloat(e.target.value) || 0, y];
                                          onUpdatePolygon(p.id, { points: newPoints });
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="flex-1 min-w-0 h-4 px-0.5 rounded border border-white/10 bg-black text-white text-[10px] outline-none text-right tabular-nums focus:border-lime-400"
                                      />
                                    </div>
                                    <div className="flex items-center gap-0.5 mt-0.5">
                                      <span className="text-[9px] text-green-400 shrink-0">Y</span>
                                      <input
                                        type="number"
                                        step={0.01}
                                        value={parseFloat(y.toFixed(2))}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          const newPoints = [...p.points] as [number, number][];
                                          newPoints[vi] = [x, parseFloat(e.target.value) || 0];
                                          onUpdatePolygon(p.id, { points: newPoints });
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="flex-1 min-w-0 h-4 px-0.5 rounded border border-white/10 bg-black text-white text-[10px] outline-none text-right tabular-nums focus:border-lime-400"
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Drawing indicator — fixed outside scroll so it's always visible while drawing */}
      {drawingPointsCount > 0 && (
        <div className="flex-shrink-0 px-4 py-2 bg-[#0c1926] border-t border-l-[3px] border-l-blue-500 border-gray-800">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-xs text-blue-400">
              {t('projects.cadProject.drawingInProgress', { count: drawingPointsCount })}
            </span>
          </div>
        </div>
      )}

      {/* Bottom bar */}
      {footer}

      {/* Type dropdown — rendered outside scroll container */}
      {expandedTypeId && (
        <div className="fixed inset-0 z-40" onClick={() => setExpandedTypeId(null)}>
          <div
            className="fixed bg-[#232323] rounded-md shadow-lg overflow-hidden min-w-[180px] z-50"
            style={{
              left: dropdownPos.left,
              ...(dropdownPos.bottom !== undefined ? { bottom: dropdownPos.bottom } : { top: dropdownPos.top }),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1 bg-[#313131] max-h-[30px]">
              <span className="text-xs font-medium text-white">{t('projects.newProject.setLayerType')}</span>
            </div>
            <div className="py-1">
              {layerTypeKeys(pedestrianLayerType, polygons.find((pp) => pp.id === expandedTypeId)?.type).map((type) => {
                const current = polygons.find((pp) => pp.id === expandedTypeId);
                return (
                  <button
                    key={type}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTypeSelect(expandedTypeId, type);
                    }}
                    className={`layer-icon-container w-full flex items-center gap-2 px-3 py-2 text-left transition ${
                      current?.type === type
                        ? 'bg-[#313131] text-white'
                        : 'text-white hover:bg-[#B3EC49] hover:text-black'
                    }`}
                  >
                    <LayerTypeIcon type={type} style={{ width: 14, height: 14 }} />
                    <span className="text-sm">{layerTypeLabel(t, type)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
